import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Icon,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  useToast,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Checkbox,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiRefreshCw,
  FiDownload,
  FiMoreVertical,
  FiTrash2,
  FiEye,
} from 'react-icons/fi';
import { canvasAPI } from '../../../services/api';

const CertificatesTab = ({ certificates, loading, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  const [isDownloading, setIsDownloading] = useState(null);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [isDownloadingMultiple, setIsDownloadingMultiple] = useState(false);
  
  const { isOpen: isRevokeOpen, onOpen: onRevokeOpen, onClose: onRevokeClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  const toast = useToast();

  // Obtener lista única de plantillas disponibles
  const availableTemplates = useMemo(() => {
    const templates = new Set();
    certificates.forEach(cert => {
      if (cert.template_name) {
        templates.add(cert.template_name);
      } else {
        templates.add('default');
      }
    });
    return Array.from(templates).sort();
  }, [certificates]);

  // Filtrar certificados
  const filteredCertificates = useMemo(() => {
    return certificates.filter(cert => {
      // Búsqueda por nombre/ID
      const matchesSearch = searchQuery === '' || 
        cert.student_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        cert.certificate_id.toLowerCase().includes(searchQuery.toLowerCase());

      // Filtro por estado
      const matchesStatus = statusFilter === 'all' || cert.status === statusFilter;

      // Filtro por tipo de plantilla
      const certTemplate = cert.template_name || 'default';
      const matchesTemplate = templateFilter === 'all' || certTemplate === templateFilter;

      return matchesSearch && matchesStatus && matchesTemplate;
    });
  }, [certificates, searchQuery, statusFilter, templateFilter]);

  // Certificados elegibles para descarga (emitidos y con PDF generado)
  const downloadableCertificates = useMemo(() => {
    return filteredCertificates.filter(
      cert => cert.pdf_generated && cert.status === 'issued'
    );
  }, [filteredCertificates]);

  // Manejar selección individual
  const handleSelectCertificate = (certId) => {
    setSelectedCertificates(prev => {
      if (prev.includes(certId)) {
        return prev.filter(id => id !== certId);
      } else {
        return [...prev, certId];
      }
    });
  };

  // Manejar selección de todos
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const downloadableIds = downloadableCertificates.map(cert => cert.id);
      setSelectedCertificates(downloadableIds);
    } else {
      setSelectedCertificates([]);
    }
  };

  // Descargar múltiples certificados
  const handleDownloadMultiple = async () => {
    if (selectedCertificates.length === 0) return;

    try {
      setIsDownloadingMultiple(true);
      
      // El backend genera automáticamente el nombre con timestamp
      await canvasAPI.downloadMultipleCertificates(selectedCertificates);
      
      toast({
        title: 'Descarga completada',
        description: `${selectedCertificates.length} certificado(s) descargado(s) exitosamente`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
      // Limpiar selección
      setSelectedCertificates([]);
    } catch (error) {
      console.error('Error downloading multiple certificates:', error);
      toast({
        title: 'Error en descarga múltiple',
        description: error.response?.data?.detail || 'No se pudieron descargar los certificados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloadingMultiple(false);
    }
  };

  const handleDownload = async (certificateId, studentName) => {
    try {
      setIsDownloading(certificateId);
      await canvasAPI.downloadCertificateByInstructor(
        certificateId,
        `certificado_${studentName.replace(/\s+/g, '_')}`
      );
      
      toast({
        title: 'Certificado descargado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast({
        title: 'Error',
        description: 'No se pudo descargar el certificado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(null);
    }
  };

  const handleOpenRevokeDialog = (certificate) => {
    setSelectedCertificate(certificate);
    setRevokeReason('');
    onRevokeOpen();
  };

  const handleRevoke = async () => {
    if (!selectedCertificate) return;

    try {
      setIsRevoking(true);
      await canvasAPI.revokeCertificate(selectedCertificate.id, revokeReason || null);
      
      toast({
        title: 'Certificado revocado',
        description: `Certificado de ${selectedCertificate.student_name} revocado exitosamente`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onRevokeClose();
      setSelectedCertificate(null);
      setRevokeReason('');
      
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error('Error revoking certificate:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo revocar el certificado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRevoking(false);
    }
  };

  const handleViewDetails = (certificate) => {
    setSelectedCertificate(certificate);
    onDetailOpen();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      issued: { color: 'green', label: 'Emitido' },
      revoked: { color: 'red', label: 'Revocado' },
      pending: { color: 'yellow', label: 'Pendiente' },
    };

    const config = statusConfig[status] || { color: 'gray', label: status };
    return <Badge colorScheme={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" thickness="4px" />
        <Text mt={4} color="gray.600">Cargando certificados...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Actions */}
            <HStack spacing={3} flexWrap="wrap">
              <Button
                leftIcon={<FiRefreshCw />}
                onClick={onRefresh}
                variant="outline"
                colorScheme="blue"
              >
                Actualizar
              </Button>
              {selectedCertificates.length > 0 && (
                <Button
                  leftIcon={<FiDownload />}
                  onClick={handleDownloadMultiple}
                  colorScheme="green"
                  isLoading={isDownloadingMultiple}
                  loadingText="Descargando..."
                >
                  Descargar Seleccionados ({selectedCertificates.length})
                </Button>
              )}
              <Text color="gray.600" fontSize="sm">
                Total: {certificates.length} certificado(s)
              </Text>
              {downloadableCertificates.length > 0 && (
                <Text color="gray.500" fontSize="sm">
                  Disponibles para descarga: {downloadableCertificates.length}
                </Text>
              )}
            </HStack>

            {/* Filters */}
            <HStack spacing={3} flexWrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por nombre o ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>

              <Select
                maxW="200px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todos los estados</option>
                <option value="issued">Emitidos</option>
                <option value="revoked">Revocados</option>
              </Select>

              <Select
                maxW="200px"
                value={templateFilter}
                onChange={(e) => setTemplateFilter(e.target.value)}
              >
                <option value="all">Todos los tipos</option>
                {availableTemplates.map(template => (
                  <option key={template} value={template}>
                    {template === 'default' ? 'Predeterminado' : template}
                  </option>
                ))}
              </Select>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Certificates Table */}
      {filteredCertificates.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          {certificates.length === 0 
            ? 'No se han emitido certificados aún' 
            : 'No se encontraron certificados con los filtros aplicados'}
        </Alert>
      ) : (
        <Card>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th width="40px">
                    <Checkbox
                      isChecked={
                        downloadableCertificates.length > 0 &&
                        selectedCertificates.length === downloadableCertificates.length
                      }
                      isIndeterminate={
                        selectedCertificates.length > 0 &&
                        selectedCertificates.length < downloadableCertificates.length
                      }
                      onChange={handleSelectAll}
                      isDisabled={downloadableCertificates.length === 0}
                    />
                  </Th>
                  <Th>ID Certificado</Th>
                  <Th>Estudiante</Th>
                  <Th>Curso</Th>
                  <Th>Tipo</Th>
                  <Th isNumeric>Calificación</Th>
                  <Th>Fecha Emisión</Th>
                  <Th>Estado</Th>
                  <Th>Emitido por</Th>
                  <Th>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredCertificates.map((cert) => {
                  const isDownloadable = cert.pdf_generated && cert.status === 'issued';
                  return (
                  <Tr key={cert.id}>
                    <Td>
                      <Checkbox
                        isChecked={selectedCertificates.includes(cert.id)}
                        onChange={() => handleSelectCertificate(cert.id)}
                        isDisabled={!isDownloadable}
                      />
                    </Td>
                    <Td>
                      <Text fontSize="sm" fontFamily="mono" fontWeight="medium">
                        {cert.certificate_id}
                      </Text>
                    </Td>
                    <Td fontWeight="medium">{cert.student_name}</Td>
                    <Td color="gray.600">{cert.course_name}</Td>
                    <Td>
                      {cert.template_name ? (
                        <Badge colorScheme="purple" fontSize="xs">
                          {cert.template_name}
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color="gray.400">Predeterminado</Text>
                      )}
                    </Td>
                    <Td isNumeric>
                      {cert.final_grade ? (
                        <Badge colorScheme="green" fontSize="sm">
                          {cert.final_grade.toFixed(1)}
                        </Badge>
                      ) : (
                        <Text fontSize="sm" color="gray.500">N/A</Text>
                      )}
                    </Td>
                    <Td fontSize="sm">
                      {new Date(cert.issue_date).toLocaleDateString('es-ES')}
                    </Td>
                    <Td>{getStatusBadge(cert.status)}</Td>
                    <Td fontSize="sm" color="gray.600">
                      {cert.issued_by}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        {cert.pdf_generated && cert.status === 'issued' && (
                          <IconButton
                            icon={<FiDownload />}
                            aria-label="Descargar"
                            size="sm"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={() => handleDownload(cert.id, cert.student_name)}
                            isLoading={isDownloading === cert.id}
                          />
                        )}
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            aria-label="Más opciones"
                            size="sm"
                            variant="ghost"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEye />}
                              onClick={() => handleViewDetails(cert)}
                            >
                              Ver detalles
                            </MenuItem>
                            {cert.status === 'issued' && (
                              <MenuItem
                                icon={<FiTrash2 />}
                                color="red.600"
                                onClick={() => handleOpenRevokeDialog(cert)}
                              >
                                Revocar certificado
                              </MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Td>
                  </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Card>
      )}

      {/* Revoke Dialog */}
      <AlertDialog
        isOpen={isRevokeOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRevokeClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Revocar Certificado
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                <Text>
                  ¿Estás seguro de que deseas revocar el certificado de{' '}
                  <strong>{selectedCertificate?.student_name}</strong>?
                </Text>
                <Text fontSize="sm" color="gray.600">
                  Esta acción no se puede deshacer.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">Razón (opcional)</FormLabel>
                  <Textarea
                    value={revokeReason}
                    onChange={(e) => setRevokeReason(e.target.value)}
                    placeholder="Escribe la razón de la revocación..."
                    size="sm"
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onRevokeClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRevoke}
                ml={3}
                isLoading={isRevoking}
                loadingText="Revocando..."
              >
                Revocar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Details Modal */}
      <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Detalles del Certificado</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedCertificate && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" color="gray.500">ID Certificado</Text>
                  <Text fontWeight="medium" fontFamily="mono">
                    {selectedCertificate.certificate_id}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Estudiante</Text>
                  <Text fontWeight="medium">{selectedCertificate.student_name}</Text>
                  {selectedCertificate.student_email && (
                    <Text fontSize="sm" color="gray.600">
                      {selectedCertificate.student_email}
                    </Text>
                  )}
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Curso</Text>
                  <Text fontWeight="medium">{selectedCertificate.course_name}</Text>
                  <Text fontSize="sm" color="gray.600">
                    Duración: {selectedCertificate.duration}
                  </Text>
                </Box>
                {selectedCertificate.final_grade && (
                  <Box>
                    <Text fontSize="sm" color="gray.500">Calificación Final</Text>
                    <Badge colorScheme="green" fontSize="lg" px={2}>
                      {selectedCertificate.final_grade.toFixed(1)}
                    </Badge>
                  </Box>
                )}
                <Box>
                  <Text fontSize="sm" color="gray.500">Fecha de Emisión</Text>
                  <Text fontWeight="medium">
                    {new Date(selectedCertificate.issue_date).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Estado</Text>
                  {getStatusBadge(selectedCertificate.status)}
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">Emitido por</Text>
                  <Text fontWeight="medium">{selectedCertificate.issued_by}</Text>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CertificatesTab;
